/**
 * Which campaigns should be reminded to get comments on their ads.
 *
 * Twin of server/lib/commentReminder.js. The browser cannot import that
 * CommonJS file, and two different bars would make the morning reminder and
 * the Campaigns link disagree. Both copies are pinned by tests to the same
 * numbers: spend strictly over $5,000 and ROAS strictly over 1.0x.
 */

export const COMMENT_SPEND = 5000;
export const COMMENT_ROAS = 1;
export const COMMENT_REMINDER_PATH = '/campaigns?needsComments=1';
export const AD_CAP = 40;

export function commentBars(raw) {
  const spend = Number.parseFloat(raw?.spend);
  const roas = Number.parseFloat(raw?.roas);
  return {
    spend: Number.isFinite(spend) && spend >= 0 ? spend : COMMENT_SPEND,
    roas: Number.isFinite(roas) && roas >= 0 ? roas : COMMENT_ROAS,
  };
}

export function campaignNeedsComments(c, rawBars) {
  const bars = commentBars(rawBars);
  const spend = Number.parseFloat(c?.spend ?? 0);
  const roas = Number.parseFloat(c?.roas ?? 0);
  if (!Number.isFinite(spend) || !Number.isFinite(roas)) return false;
  return spend > bars.spend && roas > bars.roas;
}

function adRoas(ad) {
  const explicit = Number.parseFloat(ad?.roas);
  if (Number.isFinite(explicit)) return explicit;
  const spend = Number.parseFloat(ad?.spend ?? 0);
  const revenue = Number.parseFloat(ad?.revenue ?? 0);
  if (!Number.isFinite(spend) || spend <= 0 || !Number.isFinite(revenue)) return 0;
  return revenue / spend;
}

/** An ad needs comments only when it has itself cleared both saved bars. */
export function adNeedsComments(ad, rawBars) {
  const bars = commentBars(rawBars);
  const spend = Number.parseFloat(ad?.spend ?? 0);
  const roas = adRoas(ad);
  if (!Number.isFinite(spend) || !Number.isFinite(roas)) return false;
  return spend > bars.spend && roas > bars.roas;
}

function formatSpend(n) {
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function formatRoas(n) {
  const v = Number(n);
  return Number.isInteger(v) ? v.toFixed(1) : String(Math.round(v * 100) / 100);
}

export function commentReminderNote(rawBars) {
  const bars = commentBars(rawBars);
  return `These campaigns spent over $${formatSpend(bars.spend)} this week at above ${formatRoas(bars.roas)}x ROAS. Add comments on their ads.`;
}

export function campaignOpenPath(id) {
  return `${COMMENT_REMINDER_PATH}&open=${encodeURIComponent(id)}`;
}

/** An in-app path only. Anything else is dropped rather than followed. */
export function safeAppPath(href) {
  if (typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//')) return null;
  if (href.includes('://') || href.includes('\\')) return null;
  return href;
}

export function buildCommentReminder(campaigns, adsById, rawBars) {
  const bars = commentBars(rawBars);
  const qualifying = (campaigns || []).filter((c) => campaignNeedsComments(c, bars));
  if (!qualifying.length) return null;

  const items = [];
  let adCount = 0;
  for (const c of qualifying) {
    const loaded = adsById ? adsById[c.id] : undefined;
    const ads = Array.isArray(loaded) ? loaded.filter((ad) => adNeedsComments(ad, bars)) : [];
    const campaignName = c.name || String(c.id);
    if (!ads.length) {
      items.push({ name: campaignName, campaign: campaignName, href: campaignOpenPath(c.id) });
      continue;
    }
    adCount += ads.length;
    for (const ad of ads) {
      items.push({
        name: ad.name || String(ad.id || 'Ad'),
        campaign: campaignName,
        href: campaignOpenPath(c.id),
      });
    }
  }

  const shown = items.slice(0, AD_CAP);
  const more = items.length - shown.length;
  const titleCount = adCount > 0 ? adCount : qualifying.length;
  const noun = adCount > 0
    ? (titleCount === 1 ? 'ad needs' : 'ads need')
    : (titleCount === 1 ? 'campaign needs' : 'campaigns need');
  const title = `${titleCount} ${noun} comments`;
  const lines = shown.map((it) => (
    it.campaign && it.campaign !== it.name ? `• ${it.name} — ${it.campaign}` : `• ${it.name}`
  ));
  if (more > 0) lines.push(`and ${more} more`);
  // The in-app row shows the note. The ad names are the clickable `items`,
  // and Telegram gets them in the message because it has no items list.
  const note = commentReminderNote(bars);
  const listing = [note, '', ...lines].join('\n');

  return {
    title,
    body: note,
    text: `💬 *${title}*\n\n${listing}\n\nOpen Campaigns in the dashboard and follow the link on the reminder.`,
    payload: {
      href: COMMENT_REMINDER_PATH,
      campaigns: qualifying.length,
      ads: adCount,
      items: shown,
    },
  };
}

export async function collectCommentReminder(campaigns, loadAds, rawBars) {
  const bars = commentBars(rawBars);
  const qualifying = (campaigns || []).filter((c) => campaignNeedsComments(c, bars));
  if (!qualifying.length) return null;
  const adsById = {};
  for (const c of qualifying) {
    try {
      adsById[c.id] = await loadAds(c);
    } catch {
      adsById[c.id] = null;
    }
  }
  return buildCommentReminder(qualifying, adsById, bars);
}
